import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, Globe } from "lucide-react";
import { useTranslation } from "react-i18next";
import { LANGS } from "../i18n";
import { localizePath, delocalizePath } from "../lib/urls";

export const swapLang = (code) => {
  const { pathname, search } = window.location;
  const re = new RegExp(`^/(${LANGS.map((l) => l.code).join("|")})(?=/|$)`);
  const m = pathname.match(re);
  const cur = m ? m[1] : "en";
  const rest = m ? pathname.slice(m[0].length) || "/" : pathname;
  const local = localizePath(delocalizePath(rest, cur), code);
  const target = `/${code}${local === "/" ? "" : local}`;
  if (target !== pathname) window.location.assign(target + search);
};

export const LanguageSwitcher = ({ mobile = false }) => {
  const { i18n } = useTranslation();
  const cur = (i18n.language || "en").slice(0, 2);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  if (mobile) {
    return (
      <div className="border-b border-border px-5 py-3 flex flex-wrap gap-2" data-testid="mobile-language-switcher">
        {LANGS.map((l) => (
          <button
            key={l.code}
            data-testid={`mobile-language-option-${l.code}`}
            onClick={() => swapLang(l.code)}
            className={`font-mono-tech text-xs uppercase tracking-[0.2em] px-3 py-1.5 border ${cur === l.code ? "bg-yellow-500 text-black border-yellow-500" : "text-gray-700 border-gray-300"}`}
          >
            {l.code.toUpperCase()}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="relative flex items-center self-center ml-4 mr-4" ref={ref}>
      <button
        data-testid="language-switcher-button"
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 py-2 font-mono-tech text-xs font-bold uppercase tracking-[0.25em] transition-colors ${open ? "text-gray-950 [-webkit-text-stroke:0.3px_currentColor]" : "text-gray-900 hover:text-gray-950 hover:[-webkit-text-stroke:0.3px_currentColor]"}`}
      >
        <Globe size={14} strokeWidth={2} aria-hidden="true" data-testid="language-globe-icon" />
        {cur.toUpperCase()}
        <ChevronDown size={12} className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          className="absolute right-0 top-[calc(100%+10px)] z-50 bg-white border border-border min-w-[180px] py-1.5 shadow-[0_18px_40px_rgba(0,0,0,0.12)]"
          data-testid="language-switcher-menu"
        >
          {LANGS.map((l) => {
            const active = cur === l.code;
            return (
              <button
                key={l.code}
                data-testid={`language-option-${l.code}`}
                onClick={() => { setOpen(false); swapLang(l.code); }}
                className={`flex w-full items-center justify-between px-4 py-2.5 text-left text-xs transition-colors ${active ? "text-yellow-600 font-bold" : "text-gray-600 hover:text-gray-950 hover:bg-gray-50"}`}
              >
                {l.label}
                {active && <Check size={13} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
